import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { fmtMoney } from '@/lib/format';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import {
  Users, FileText, Receipt as ReceiptIcon, Wallet, Loader2, Search as SearchIcon,
} from 'lucide-react';

/**
 * GlobalSearch — Command palette (Cmd/Ctrl+K) that searches across clients,
 * invoices, payment receipts and expenses. Debounced 200ms.
 *
 * Usage: Mount once at the top of the app (AppShell). It listens globally
 * for the keyboard shortcut and exposes an imperative open() via a ref if
 * the trigger is placed elsewhere.
 */
export function GlobalSearch({ open, onOpenChange }) {
  const nav = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ clients: [], invoices: [], payment_receipts: [], expenses: [] });
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced fetch
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults({ clients: [], invoices: [], payment_receipts: [], expenses: [] });
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        if (abortRef.current) abortRef.current.abort?.();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        const { data } = await api.get('/search', { params: { q: query, limit: 6 }, signal: ctrl.signal });
        setResults(data.results || { clients: [], invoices: [], payment_receipts: [], expenses: [] });
      } catch (e) {
        if (e?.name !== 'CanceledError' && e?.name !== 'AbortError') {
          setResults({ clients: [], invoices: [], payment_receipts: [], expenses: [] });
        }
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, open]);

  // Clear when closed
  useEffect(() => {
    if (!open) { setQuery(''); setResults({ clients: [], invoices: [], payment_receipts: [], expenses: [] }); }
  }, [open]);

  const go = useCallback((path) => {
    onOpenChange(false);
    setTimeout(() => nav(path), 30);
  }, [nav, onOpenChange]);

  const totalCount = (results.clients?.length || 0) + (results.invoices?.length || 0) + (results.payment_receipts?.length || 0) + (results.expenses?.length || 0);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Buscar clientes, facturas, recibos, gastos…"
        value={query}
        onValueChange={setQuery}
        data-testid="global-search-input"
      />
      <CommandList data-testid="global-search-list">
        {loading && (
          <div className="flex items-center gap-2 justify-center py-4 text-xs text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Buscando…
          </div>
        )}
        {!loading && query.trim() && totalCount === 0 && (
          <CommandEmpty data-testid="global-search-empty">Sin resultados para "{query}"</CommandEmpty>
        )}
        {!loading && !query.trim() && (
          <div className="px-4 py-6 text-center text-xs text-slate-500 flex flex-col items-center gap-2">
            <SearchIcon className="h-6 w-6 text-slate-300" />
            <div>Escriba para buscar en todo su bufete.</div>
            <div className="text-[10px] text-slate-400">Tip: <kbd className="rounded border bg-slate-100 px-1">⌘K</kbd> / <kbd className="rounded border bg-slate-100 px-1">Ctrl+K</kbd> para abrir</div>
          </div>
        )}

        {(results.clients || []).length > 0 && (
          <CommandGroup heading={`Clientes · ${results.clients.length}`}>
            {results.clients.map(c => (
              <CommandItem
                key={`client-${c.id}`}
                value={`client-${c.id}-${c.full_name}`}
                onSelect={() => go(`/clientes/${c.id}`)}
                data-testid={`global-search-client-${c.id}`}
              >
                <Users className="h-4 w-4 mr-2 text-slate-500" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">{c.full_name}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {c.email || c.phone || '—'}
                    {c.client_matter_id && <span className="ml-2 font-mono text-slate-400">{c.client_matter_id}</span>}
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {(results.invoices || []).length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={`Facturas · ${results.invoices.length}`}>
              {results.invoices.map(r => (
                <CommandItem
                  key={`inv-${r.id}`}
                  value={`inv-${r.id}-${r.receipt_number}`}
                  onSelect={() => go(`/recibos/${r.id}`)}
                  data-testid={`global-search-invoice-${r.id}`}
                >
                  <FileText className="h-4 w-4 mr-2 text-slate-500" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">
                      <span className="font-mono text-xs mr-1">{r.receipt_number}</span>
                      <span className="text-slate-600">{r.client_name}</span>
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-2">
                      <span>{fmtMoney(r.total)}</span>
                      {r.balance_due > 0 && <Badge variant="outline" className="h-4 text-[10px] px-1 border-amber-300 text-amber-800">Debe {fmtMoney(r.balance_due)}</Badge>}
                      {r.status && <Badge variant="outline" className="h-4 text-[10px] px-1">{r.status}</Badge>}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {(results.payment_receipts || []).length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={`Recibos de pago · ${results.payment_receipts.length}`}>
              {results.payment_receipts.map(r => (
                <CommandItem
                  key={`pr-${r.id}`}
                  value={`pr-${r.id}-${r.receipt_number}`}
                  onSelect={() => go(`/recibos-pago`)}
                  data-testid={`global-search-payment-receipt-${r.id}`}
                >
                  <ReceiptIcon className="h-4 w-4 mr-2 text-slate-500" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">
                      <span className="font-mono text-xs mr-1">{r.receipt_number}</span>
                      <span className="text-slate-600">{r.client_name}</span>
                    </div>
                    <div className="text-xs text-slate-500">{fmtMoney(r.amount)}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {(results.expenses || []).length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={`Gastos · ${results.expenses.length}`}>
              {results.expenses.map(e => (
                <CommandItem
                  key={`exp-${e.id}`}
                  value={`exp-${e.id}-${e.vendor}`}
                  onSelect={() => go(`/gastos`)}
                  data-testid={`global-search-expense-${e.id}`}
                >
                  <Wallet className="h-4 w-4 mr-2 text-slate-500" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">{e.vendor || e.description}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {e.category && <span className="mr-2">{e.category}</span>}
                      {fmtMoney(e.amount)}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
